import { useState } from 'react';

import { AnimatePresence, motion } from 'motion/react';

export function ScreenshotGallery({ screenshots, accentColor = '#2973a3', urlPath = 'dashboard' }) {
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <div className="relative">
      {/* Tab Navigation. Galleries run to eight tabs, which is wider than a
          phone: the strip scrolls horizontally there and only centres once it
          has room, otherwise centring pushes the leading tabs out of reach. */}
      {screenshots.length > 1 && (
        <div className="flex justify-start gap-2 mb-6 overflow-x-auto md:flex-wrap md:justify-center">
          {screenshots.map((screenshot, index) => (
            <button
              key={index}
              style={{
                backgroundColor: activeIndex === index ? accentColor : undefined,
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 shrink-0 whitespace-nowrap ${
                activeIndex === index
                  ? 'text-white shadow-md'
                  : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
              }`}
              onClick={() => setActiveIndex(index)}
            >
              {screenshot.label}
            </button>
          ))}
        </div>
      )}

      {/* Browser Frame */}
      <motion.div
        className="relative"
        viewport={{ once: true }}
        transition={{ duration: 0.8, delay: 0.2 }}
        initial={{ opacity: 0, y: 40, scale: 0.95 }}
        whileInView={{ opacity: 1, y: 0, scale: 1 }}
      >
        <div className="bg-slate-800 rounded-t-xl px-4 py-3 flex items-center gap-2">
          <div className="flex gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500" />
            <div className="w-3 h-3 rounded-full bg-yellow-500" />
            <div className="w-3 h-3 rounded-full bg-green-500" />
          </div>

          <div className="flex-1 mx-4">
            <div className="bg-slate-700 rounded-md px-4 py-1.5 text-sm text-slate-300 text-center max-w-md mx-auto">
              app.secureonelabs.com/
              {urlPath}
            </div>
          </div>
        </div>

        {/* Screenshot Container with Animation. The container matches the
            screenshots' own chrome so the cross-fade between two dark slides
            doesn't flash a white panel between them. */}
        <div className="bg-slate-900 rounded-b-xl shadow-2xl border border-slate-200 overflow-hidden">
          <AnimatePresence mode="wait">
            <motion.img
              width={1920}
              height={1080}
              key={activeIndex}
              exit={{ opacity: 0 }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="w-full h-auto"
              transition={{ duration: 0.3 }}
              src={screenshots[activeIndex].src}
              alt={screenshots[activeIndex].alt}
              loading={activeIndex === 0 ? 'eager' : 'lazy'}
            />
          </AnimatePresence>
        </div>

        {/* Decorative Elements */}
        <div
          style={{ backgroundColor: accentColor }}
          className="absolute -bottom-6 -left-6 w-24 h-24 rounded-full opacity-20"
        />

        <div
          style={{ backgroundColor: accentColor }}
          className="absolute -top-6 -right-6 w-32 h-32 rounded-full opacity-10"
        />
      </motion.div>

      {/* Dots Navigation (mobile) */}
      {screenshots.length > 1 && (
        <div className="flex justify-center gap-2 mt-4 md:hidden">
          {screenshots.map((_, index) => (
            <button
              key={index}
              style={{
                backgroundColor: activeIndex === index ? accentColor : undefined,
              }}
              className={`w-2 h-2 rounded-full transition-all ${
                activeIndex === index ? 'w-6' : 'bg-gray-300'
              }`}
              onClick={() => setActiveIndex(index)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
